/**
 * A section that opens to read more: a chevron that turns and a body under it. The caller
 * places it inline under a header, or pinned to the bottom of a pane, where the body grows
 * upward out of the floor.
 */

import { useState, type ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Reveal } from '@/modules/simulator/ui/reveal';

interface DisclosureProps {
	label: string;
	/** What the toggle reads as once open; defaults to the closed label. */
	openLabel?: string;
	className?: string;
	/** Caps the body and scrolls it, for a pane that must not be pushed off screen. */
	bodyClassName?: string;
	children: ReactNode;
}

export const Disclosure = ({
	label,
	openLabel,
	className,
	bodyClassName,
	children
}: DisclosureProps) => {
	const [open, setOpen] = useState(false);
	return (
		<div className={cn('flex flex-col', className)}>
			<button
				type='button'
				onClick={() => setOpen((o) => !o)}
				aria-expanded={open}
				className='flex w-full cursor-pointer items-center gap-1.5 px-3 py-2 text-left text-xs leading-4 font-medium text-text-body transition-colors hover:bg-gray-100/60'
			>
				<ChevronRight
					strokeWidth={1.75}
					className={cn('size-3.5 shrink-0 text-gray-900 transition-transform duration-200', open && 'rotate-90')}
				/>
				{open ? (openLabel ?? label) : label}
			</button>
			<Reveal open={open}>
				<div className={cn('px-3 pb-3', bodyClassName)}>{children}</div>
			</Reveal>
		</div>
	);
};
